"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { getPostAuthRedirect } from "@/components/PostAuthRedirectGuard";
import { usePricingModal } from "@/contexts/PricingModalContext";
import type { PeeqdProfileData } from "@/libs/peeqdProfilePreview";
import type { ToolPageView } from "@/libs/tracking/resolveToolPageView";
import type {
  FollowChangeItem,
  RelationshipItem,
  ToolDashboardInitialState,
  TrackedAccountSummary,
} from "@/components/peeqd/tracking/types";
import { FOLLOW_LIST_DISPLAY_LIMIT } from "@/libs/tracking/constants";
import { useScrapeReadiness } from "@/hooks/useScrapeReadiness";
import type { ScrapeReadiness } from "@/libs/tracking/scrapeReadiness";
import { trackFunnelEvent } from "@/libs/ga";

export interface ToolDashboardState {
  detailLoading: boolean;
  /** True while resolving dashboard after sign-in - hide landing, show bootstrap shell. */
  isBootstrapping: boolean;
  pageView: ToolPageView;
  showDashboard: boolean;
  accounts: TrackedAccountSummary[];
  account: TrackedAccountSummary | null;
  selectedAccountId: string | null;
  changelog: FollowChangeItem[];
  followers: RelationshipItem[];
  following: RelationshipItem[];
  followersTrackedCount: number;
  followingTrackedCount: number;
  isScraping: boolean;
  listsReady: boolean;
  activityReady: boolean;
  likesReady: boolean;
  isCollecting: boolean;
  scrapeReadiness: ScrapeReadiness;
  profiles: Record<string, PeeqdProfileData>;
  connectLoading: boolean;
  connectError: string | null;
  selectAccount: (id: string) => Promise<void>;
  connectAccount: (username: string) => Promise<void>;
  refresh: () => Promise<void>;
}

function listsAreReady(account: TrackedAccountSummary | null): boolean {
  return !!account?.scrape.readiness?.lists;
}

function activityIsReady(account: TrackedAccountSummary | null): boolean {
  return !!account?.scrape.readiness?.activitySummary;
}

function emptyState(): ToolDashboardInitialState {
  return {
    pageView: "landing",
    showDashboard: false,
    accounts: [],
    selectedAccountId: null,
    account: null,
    changelog: [],
    followers: [],
    following: [],
    followersTrackedCount: 0,
    followingTrackedCount: 0,
    isScraping: false,
    profiles: {},
  };
}

function scrapeSnapshotKey(
  scrape: TrackedAccountSummary["scrape"] | null | undefined
): string {
  if (!scrape) return "";
  return JSON.stringify({
    pendingJobs: scrape.pendingJobs,
    runningJobs: scrape.runningJobs,
    lastSuccessAt: scrape.lastSuccessAt,
    isScraping: scrape.isScraping,
    readiness: scrape.readiness,
  });
}

export function useToolDashboard(
  initial: ToolDashboardInitialState = emptyState()
): ToolDashboardState {
  const { status } = useSession();
  const { postAuthLoading, clearPostAuthLoading } = useAuthModal();
  const { openPricingModal } = usePricingModal();
  const [detailLoading, setDetailLoading] = useState(
    () => initial.account != null && !listsAreReady(initial.account)
  );
  const [accounts, setAccounts] = useState(initial.accounts);
  const [account, setAccount] = useState(initial.account);
  const [selectedAccountId, setSelectedAccountId] = useState(initial.selectedAccountId);
  const [changelog, setChangelog] = useState(initial.changelog);
  const [followers, setFollowers] = useState(initial.followers);
  const [following, setFollowing] = useState(initial.following);
  const [followersTrackedCount, setFollowersTrackedCount] = useState(
    initial.followersTrackedCount ?? 0
  );
  const [followingTrackedCount, setFollowingTrackedCount] = useState(
    initial.followingTrackedCount ?? 0
  );
  const [profiles, setProfiles] = useState<Record<string, PeeqdProfileData>>(() => {
    const map = { ...initial.profiles };
    for (const row of initial.accounts) {
      if (row.profile) map[row.id] = row.profile;
    }
    return map;
  });
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(initial.selectedAccountId);
  const hydratedRef = useRef(initial.pageView === "dashboard");
  const prevStatusRef = useRef<string | null>(null);
  const detailFetchedForRef = useRef<string | null>(null);

  const fetchDetail = useCallback(async (accountId: string) => {
    const [changelogRes, followersRes, followingRes] = await Promise.all([
      fetch(`/api/tracking/accounts/${accountId}/changelog?limit=${FOLLOW_LIST_DISPLAY_LIMIT}`),
      fetch(
        `/api/tracking/accounts/${accountId}/relationships?side=followers&limit=${FOLLOW_LIST_DISPLAY_LIMIT}`
      ),
      fetch(
        `/api/tracking/accounts/${accountId}/relationships?side=following&limit=${FOLLOW_LIST_DISPLAY_LIMIT}`
      ),
    ]);

    if (!changelogRes.ok || !followersRes.ok || !followingRes.ok) {
      throw new Error("Failed to load dashboard data");
    }

    const [changelogData, followersData, followingData] = await Promise.all([
      changelogRes.json(),
      followersRes.json(),
      followingRes.json(),
    ]);

    setChangelog(changelogData.changes ?? []);
    setFollowers(followersData.relationships ?? []);
    setFollowing(followingData.relationships ?? []);
    setFollowersTrackedCount(followersData.totalCount ?? followersData.count ?? 0);
    setFollowingTrackedCount(followingData.totalCount ?? followingData.count ?? 0);
  }, []);

  const applySelection = useCallback(
    async (list: TrackedAccountSummary[], accountId: string | null, withDetailLoading: boolean) => {
      const selected = list.find((a) => a.id === accountId) ?? null;
      setAccount(selected);

      if (!selected) {
        setChangelog([]);
        setFollowers([]);
        setFollowing([]);
        setFollowersTrackedCount(0);
        setFollowingTrackedCount(0);
        setDetailLoading(false);
        return;
      }

      const waitingForLists = !listsAreReady(selected);
      if (withDetailLoading || waitingForLists) {
        setDetailLoading(true);
        if (waitingForLists) {
          setChangelog([]);
          setFollowers([]);
          setFollowing([]);
          setFollowersTrackedCount(0);
          setFollowingTrackedCount(0);
        }
      }

      if (waitingForLists) {
        return;
      }

      try {
        await fetchDetail(selected.id);
      } catch (err) {
        console.error("[useToolDashboard] detail", err);
        setChangelog([]);
        setFollowers([]);
        setFollowing([]);
        setFollowersTrackedCount(0);
        setFollowingTrackedCount(0);
      } finally {
        if (withDetailLoading) setDetailLoading(false);
      }
    },
    [fetchDetail]
  );

  const clearState = useCallback(() => {
    selectedIdRef.current = null;
    setSelectedAccountId(null);
    setAccounts([]);
    setAccount(null);
    setChangelog([]);
    setFollowers([]);
    setFollowing([]);
    setFollowersTrackedCount(0);
    setFollowingTrackedCount(0);
    setProfiles({});
    setConnectError(null);
    setConnectLoading(false);
    setDetailLoading(false);
  }, []);

  const refresh = useCallback(
    async ({ bootstrap = false }: { bootstrap?: boolean } = {}) => {
      if (status === "unauthenticated") {
        clearState();
        if (bootstrap) clearPostAuthLoading();
        return;
      }

      if (status === "loading") {
        return;
      }

      try {
        const res = await fetch("/api/tracking/accounts");
        if (!res.ok) throw new Error("Failed to list accounts");
        const data = await res.json();
        const profileMap: Record<string, PeeqdProfileData> = {};
        const list: TrackedAccountSummary[] = (data.accounts ?? []).map(
          (row: TrackedAccountSummary & { profile?: PeeqdProfileData | null }) => {
            if (row.profile) profileMap[row.id] = row.profile;
            return {
              id: row.id,
              instagramUsername: row.instagramUsername,
              planTier: row.planTier,
              status: row.status,
              enabledTasks: row.enabledTasks,
              createdAt: row.createdAt,
              avatarUrl: row.avatarUrl ?? row.profile?.avatarUrl ?? null,
              profile: row.profile ?? null,
              scrape: row.scrape,
            };
          }
        );
        setAccounts(list);

        if (Object.keys(profileMap).length > 0) {
          setProfiles((prev) => ({ ...prev, ...profileMap }));
        }

        let activeId = selectedIdRef.current;
        if (!activeId || !list.some((a) => a.id === activeId)) {
          activeId = list.find((a) => listsAreReady(a))?.id ?? list[0]?.id ?? null;
        }

        selectedIdRef.current = activeId;
        setSelectedAccountId(activeId);
        const selected = activeId ? (list.find((a) => a.id === activeId) ?? null) : null;
        setAccount(selected);

        if (bootstrap) clearPostAuthLoading();

        const waitingForLists = selected != null && !listsAreReady(selected);
        const loadingDetail =
          waitingForLists ||
          bootstrap ||
          (activeId != null &&
            followers.length === 0 &&
            following.length === 0 &&
            changelog.length === 0 &&
            selected != null &&
            listsAreReady(selected));

        if (waitingForLists) {
          setDetailLoading(true);
          return;
        }

        await applySelection(list, activeId, loadingDetail);
      } catch (err) {
        console.error("[useToolDashboard]", err);
        if (bootstrap) clearPostAuthLoading();
      }
    },
    [status, applySelection, clearState, clearPostAuthLoading, followers.length, following.length, changelog.length]
  );

  const selectAccount = useCallback(
    async (id: string) => {
      if (id === selectedIdRef.current) return;
      selectedIdRef.current = id;
      setSelectedAccountId(id);
      await applySelection(accounts, id, true);
    },
    [accounts, applySelection]
  );

  const connectAccount = useCallback(
    async (username: string) => {
      const normalized = username.trim().replace(/^@+/, "");
      setConnectLoading(true);
      setConnectError(null);

      trackFunnelEvent("tool_engagement", "account_connect_started", {
        username: normalized,
      });

      try {
        const res = await fetch("/api/tracking/accounts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ instagramUsername: normalized }),
        });
        const body = await res.json();
        if (!res.ok) {
          if (res.status === 403 && body.code === "subscription_required") {
            trackFunnelEvent("monetization", "subscription_required", {
              username: normalized,
              source: "connect_account",
            });
            openPricingModal({
              source: "subscription_required",
              username: normalized,
            });
            return;
          }
          throw new Error(body.error ?? "Failed to add account");
        }

        const nextAccount = body.account as TrackedAccountSummary;
        const profile = body.profile as PeeqdProfileData | undefined;
        if (profile) {
          nextAccount.profile = profile;
          if (!nextAccount.avatarUrl) nextAccount.avatarUrl = profile.avatarUrl ?? null;
        }

        setAccounts((prev) => {
          const existing = prev.find((a) => a.id === nextAccount.id);
          if (existing) {
            return prev.map((a) => (a.id === nextAccount.id ? nextAccount : a));
          }
          return [nextAccount, ...prev];
        });

        if (profile) {
          setProfiles((prev) => ({ ...prev, [nextAccount.id]: profile }));
        }

        selectedIdRef.current = nextAccount.id;
        setSelectedAccountId(nextAccount.id);
        setAccount(nextAccount);
        setChangelog([]);
        setFollowers([]);
        setFollowing([]);
        setFollowersTrackedCount(0);
        setFollowingTrackedCount(0);
        setDetailLoading(true);

        trackFunnelEvent("tool_engagement", "account_connect_succeeded", {
          username: normalized,
          account_id: nextAccount.id,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to add account";
        setConnectError(message);
        trackFunnelEvent("tool_engagement", "account_connect_failed", {
          username: normalized,
          error: message,
        });
      } finally {
        setConnectLoading(false);
      }
    },
    [openPricingModal]
  );

  useEffect(() => {
    if (status === "loading") return;

    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    if (status === "unauthenticated") {
      clearState();
      hydratedRef.current = false;
      return;
    }

    const justLoggedIn = prev === "unauthenticated" && status === "authenticated";
    const becameAuthenticated =
      status === "authenticated" &&
      (prev === "unauthenticated" || prev === "loading" || prev === null);

    if (!hydratedRef.current || becameAuthenticated) {
      if (becameAuthenticated && getPostAuthRedirect()) {
        return;
      }
      hydratedRef.current = true;
      void refresh({ bootstrap: justLoggedIn });
    }
  }, [status, refresh, clearState]);

  const scrapePoller = useScrapeReadiness(account?.id ?? null, {
    enabled: status === "authenticated" && account != null,
    initialScrape: account?.scrape ?? null,
  });

  useEffect(() => {
    const polled = scrapePoller.status;
    if (!polled || !account) return;

    const nextScrape = polled;
    setAccount((prev) => {
      if (prev?.id !== account.id) return prev;
      if (scrapeSnapshotKey(prev.scrape) === scrapeSnapshotKey(nextScrape)) {
        return prev;
      }
      return { ...prev, scrape: nextScrape };
    });
    setAccounts((prev) =>
      prev.map((row) => {
        if (row.id !== account.id) return row;
        if (scrapeSnapshotKey(row.scrape) === scrapeSnapshotKey(nextScrape)) {
          return row;
        }
        return { ...row, scrape: nextScrape };
      })
    );
  }, [scrapePoller.status, account?.id]);

  useEffect(() => {
    if (!account || status !== "authenticated") return;

    const listsReady =
      scrapePoller.listsReady || Boolean(account.scrape.readiness?.lists);

    if (!listsReady) {
      detailFetchedForRef.current = null;
      return;
    }

    const snapshotKey = scrapeSnapshotKey(account.scrape);
    const fetchKey = `${account.id}:${snapshotKey}`;
    if (detailFetchedForRef.current === fetchKey) return;

    detailFetchedForRef.current = fetchKey;
    setDetailLoading(false);
    void fetchDetail(account.id);
  }, [
    account?.id,
    scrapeSnapshotKey(account?.scrape ?? null),
    status,
    scrapePoller.listsReady,
    scrapePoller.activityReady,
    scrapePoller.likesReady,
    fetchDetail,
  ]);

  useEffect(() => {
    if (!account?.id) detailFetchedForRef.current = null;
  }, [account?.id]);

  const isScraping = scrapePoller.isScraping || !!account?.scrape.isScraping;

  const pageView: ToolPageView =
    status === "authenticated"
      ? "dashboard"
      : status === "unauthenticated"
        ? "landing"
        : initial.pageView;

  const showDashboard =
    pageView === "dashboard" &&
    !(status === "authenticated" && getPostAuthRedirect());

  const dashboardTrackedRef = useRef(false);
  useEffect(() => {
    if (status !== "authenticated" || !showDashboard) {
      dashboardTrackedRef.current = false;
      return;
    }
    if (dashboardTrackedRef.current) return;
    dashboardTrackedRef.current = true;
    trackFunnelEvent("tool_engagement", "dashboard_viewed", {
      account_count: accounts.length,
      has_selected_account: !!selectedAccountId,
    });
  }, [status, showDashboard, accounts.length, selectedAccountId]);

  return {
    detailLoading,
    isBootstrapping: postAuthLoading,
    pageView,
    showDashboard,
    accounts,
    account,
    selectedAccountId,
    changelog,
    followers,
    following,
    followersTrackedCount,
    followingTrackedCount,
    isScraping,
    listsReady: scrapePoller.listsReady,
    activityReady: scrapePoller.activityReady,
    likesReady: scrapePoller.likesReady,
    isCollecting: scrapePoller.isCollecting,
    scrapeReadiness: scrapePoller.readiness,
    profiles,
    connectLoading,
    connectError,
    selectAccount,
    connectAccount,
    refresh,
  };
}
